/**
 * /quality/measure/:measureId — sortable leaderboard of every U.S. hospital that reports
 * a given CMS quality measure (e.g. Hybrid_HWM mortality, MORT_30_HF heart-failure mortality).
 *
 * Pulls live from `hospital_quality_measures` JOINed to `hospitals`, plus aggregates
 * from `national_quality_index` for context (national / state median + IQR).
 *
 * Search by hospital name + filter by state. Default sort: best score first
 * (ascending — lower is better for mortality/readmission/infection). Switchable to
 * worst-first or alphabetical.
 */
import { Helmet } from "react-helmet-async";
import { useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Search, ArrowUpDown, MapPin, Loader2, ExternalLink } from "lucide-react";
import { useBrand } from "@/config/brands";
import { useTheme } from "@/contexts/ThemeContext";
import { titleCaseHospital } from "@/lib/hospitalName";

// Friendly labels for the most-watched CMS measures.
const MEASURE_LABELS: Record<string, { name: string; what: string; unit: string; lowerIsBetter: boolean }> = {
  Hybrid_HWM:     { name: "Hospital-wide mortality (30-day)",   what: "30-day risk-adjusted death rate after admission, all causes.", unit: "%",       lowerIsBetter: true },
  Hybrid_HWR:     { name: "Hospital-wide readmission (30-day)", what: "30-day all-cause unplanned readmission rate.",                  unit: "%",       lowerIsBetter: true },
  MORT_30_AMI:    { name: "Heart attack mortality (30-day)",    what: "Death rate within 30 days of heart attack admission.",         unit: "%",       lowerIsBetter: true },
  MORT_30_HF:     { name: "Heart failure mortality (30-day)",   what: "Death rate within 30 days of heart failure admission.",        unit: "%",       lowerIsBetter: true },
  MORT_30_PN:     { name: "Pneumonia mortality (30-day)",       what: "Death rate within 30 days of pneumonia admission.",            unit: "%",       lowerIsBetter: true },
  MORT_30_COPD:   { name: "COPD mortality (30-day)",            what: "Death rate within 30 days of COPD admission.",                 unit: "%",       lowerIsBetter: true },
  MORT_30_STK:    { name: "Stroke mortality (30-day)",          what: "Death rate within 30 days of stroke admission.",               unit: "%",       lowerIsBetter: true },
  MORT_30_CABG:   { name: "Coronary bypass mortality (30-day)", what: "Death rate within 30 days of coronary bypass surgery.",        unit: "%",       lowerIsBetter: true },
  READM_30_AMI:   { name: "Heart attack readmission (30-day)",  what: "Unplanned readmission within 30 days of heart attack stay.",   unit: "%",       lowerIsBetter: true },
  READM_30_HF:    { name: "Heart failure readmission (30-day)", what: "Unplanned readmission within 30 days of heart failure stay.",  unit: "%",       lowerIsBetter: true },
  READM_30_PN:    { name: "Pneumonia readmission (30-day)",     what: "Unplanned readmission within 30 days of pneumonia stay.",      unit: "%",       lowerIsBetter: true },
  READM_30_COPD:  { name: "COPD readmission (30-day)",          what: "Unplanned readmission within 30 days of COPD stay.",           unit: "%",       lowerIsBetter: true },
  READM_30_STK:   { name: "Stroke readmission (30-day)",        what: "Unplanned readmission within 30 days of stroke stay.",         unit: "%",       lowerIsBetter: true },
  READM_30_HIP_KNEE: { name: "Hip/knee replacement readmission (30-day)", what: "Unplanned readmission within 30 days of hip or knee replacement.", unit: "%", lowerIsBetter: true },
  COMP_HIP_KNEE:  { name: "Hip/knee complication rate",          what: "Rate of serious complications after hip or knee replacement.", unit: "%",       lowerIsBetter: true },
  PSI_06:         { name: "Iatrogenic pneumothorax rate",        what: "Rate of accidental lung punctures (per 1,000 stays).",         unit: " per 1k", lowerIsBetter: true },
  PSI_08:         { name: "In-hospital fall fracture rate",      what: "Rate of falls causing fracture (per 1,000 stays).",            unit: " per 1k", lowerIsBetter: true },
  OP_18b:         { name: "ER median wait time",                  what: "Median time patients spent in the ER before leaving.",         unit: " min",    lowerIsBetter: true },
  OP_22:          { name: "ER patients leaving without seen",    what: "Percent of ER patients who left before being seen.",            unit: "%",       lowerIsBetter: true },
};

const STATE_NAMES: Record<string, string> = {
  AL:'Alabama',AK:'Alaska',AZ:'Arizona',AR:'Arkansas',CA:'California',CO:'Colorado',CT:'Connecticut',DE:'Delaware',DC:'D.C.',FL:'Florida',GA:'Georgia',HI:'Hawaii',ID:'Idaho',IL:'Illinois',IN:'Indiana',IA:'Iowa',KS:'Kansas',KY:'Kentucky',LA:'Louisiana',ME:'Maine',MD:'Maryland',MA:'Massachusetts',MI:'Michigan',MN:'Minnesota',MS:'Mississippi',MO:'Missouri',MT:'Montana',NE:'Nebraska',NV:'Nevada',NH:'New Hampshire',NJ:'New Jersey',NM:'New Mexico',NY:'New York',NC:'North Carolina',ND:'North Dakota',OH:'Ohio',OK:'Oklahoma',OR:'Oregon',PA:'Pennsylvania',RI:'Rhode Island',SC:'South Carolina',SD:'South Dakota',TN:'Tennessee',TX:'Texas',UT:'Utah',VT:'Vermont',VA:'Virginia',WA:'Washington',WV:'West Virginia',WI:'Wisconsin',WY:'Wyoming',PR:'Puerto Rico',
};

export default function QualityMeasureLeaderboard() {
  const { measureId = "" } = useParams<{ measureId: string }>();
  const brand = useBrand();
  const { isDark } = useTheme();
  const isAhp = brand.key === 'ahp';

  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState<string>('all');
  const [sortDir, setSortDir] = useState<'best' | 'worst' | 'name'>('best');
  const PAGE = 50;
  const [page, setPage] = useState(1);

  // Pull every reporting hospital's score for this measure, plus the joined hospital row.
  const { data: rows, isLoading } = useQuery({
    queryKey: ['quality-measure-leaderboard', measureId],
    enabled: !!measureId,
    staleTime: 1000 * 60 * 60,
    queryFn: async () => {
      // Bulk-paged because PostgREST defaults cap at 1000 rows.
      const out: Array<{
        hospital_id: string;
        score: number;
        denominator: number | null;
        measure_name: string;
        compared_to_national: string | null;
        hospitals: { name: string; city: string; state: string } | null;
      }> = [];
      const PAGE_SIZE = 1000;
      for (let from = 0; ; from += PAGE_SIZE) {
        const { data, error } = await supabase
          .from('hospital_quality_measures')
          .select('hospital_id, score, denominator, measure_name, compared_to_national, hospitals!inner(name, city, state)')
          .eq('measure_id', measureId)
          .not('score', 'is', null)
          .range(from, from + PAGE_SIZE - 1);
        if (error) throw error;
        if (!data || data.length === 0) break;
        out.push(...(data as any[]));
        if (data.length < PAGE_SIZE) break;
      }
      return out;
    },
  });

  // National + state aggregates for context.
  const { data: aggregates } = useQuery({
    queryKey: ['quality-measure-aggregates', measureId],
    enabled: !!measureId,
    staleTime: 1000 * 60 * 60,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('national_quality_index')
        .select('geography_type, geography_id, hospital_count, min_score, p25_score, median_score, p75_score, max_score')
        .eq('measure_id', measureId);
      if (error) throw error;
      return data || [];
    },
  });
  const national = (aggregates || []).find(a => a.geography_type === 'national');
  const stateAggs: Record<string, any> = {};
  (aggregates || []).forEach(a => { if (a.geography_type === 'state' && a.geography_id) stateAggs[a.geography_id] = a; });

  const meta = MEASURE_LABELS[measureId] || { name: measureId.replace(/_/g, ' '), what: 'CMS Hospital Compare measure.', unit: '', lowerIsBetter: true };

  const filtered = useMemo(() => {
    let arr = (rows || []).filter(r => r.hospitals);
    if (search.trim()) {
      const q = search.toLowerCase();
      arr = arr.filter(r => r.hospitals!.name.toLowerCase().includes(q) || (r.hospitals!.city || '').toLowerCase().includes(q));
    }
    if (stateFilter !== 'all') arr = arr.filter(r => r.hospitals!.state === stateFilter);
    if (sortDir === 'name') arr.sort((a, b) => a.hospitals!.name.localeCompare(b.hospitals!.name));
    else {
      // best = ascending if lowerIsBetter, descending otherwise. worst = inverse.
      const ascending = (sortDir === 'best') === meta.lowerIsBetter;
      arr.sort((a, b) => ascending ? a.score - b.score : b.score - a.score);
    }
    return arr;
  }, [rows, search, stateFilter, sortDir, meta.lowerIsBetter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE));
  const visibleRows = filtered.slice((page - 1) * PAGE, page * PAGE);
  const states = useMemo(() => Array.from(new Set((rows || []).map(r => r.hospitals?.state).filter(Boolean) as string[])).sort(), [rows]);

  // Tone for an individual score relative to national p25/p75.
  const toneFor = (score: number) => {
    if (!national || national.p25_score == null || national.p75_score == null) return 'neutral';
    const p25 = Number(national.p25_score), p75 = Number(national.p75_score);
    if (meta.lowerIsBetter) {
      if (score < p25) return 'good';
      if (score > p75) return 'bad';
      return 'neutral';
    }
    if (score > p75) return 'good';
    if (score < p25) return 'bad';
    return 'neutral';
  };
  const toneColors = isDark
    ? { good: 'text-emerald-300', bad: 'text-red-300', neutral: 'text-amber-200' }
    : { good: 'text-emerald-700', bad: 'text-red-700', neutral: 'text-amber-700' };

  const seoTitle = `${meta.name} — Hospital Leaderboard | ${brand.name}`;
  const seoDesc = `${meta.what} See how every U.S. hospital that reports this measure compares — sortable, searchable, with state and national medians for context.`;

  return (
    <div className="min-h-screen" style={{ backgroundColor: isAhp ? (isDark ? '#0a1628' : '#FAF9F6') : undefined }}>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDesc} />
        <meta name="robots" content="index, follow" />
      </Helmet>

      {/* Extra pb-* on the bottom so the table + pagination don't sit
          underneath the global AppFooter mobile-nav bar at the bottom of
          the viewport. */}
      <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8 sm:py-12 pb-32 sm:pb-40">
        <Link to="/hospital-rankings">
          <Button variant="ghost" size="sm" className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to rankings
          </Button>
        </Link>

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl sm:text-4xl font-bold mb-2" style={{ fontFamily: "'Instrument Serif', Georgia, serif", fontWeight: 400, letterSpacing: '-0.5px', color: isAhp ? (isDark ? '#FAF9F6' : '#1B1B1B') : undefined }}>
            {meta.name}
          </h1>
          <p className="text-sm text-muted-foreground max-w-3xl">{meta.what} Source: CMS Hospital Compare ({measureId}).</p>
        </div>

        {/* National + cohort stats — context strip */}
        {national && (
          <Card className="p-4 mb-6 grid grid-cols-2 sm:grid-cols-5 gap-3 text-center">
            <div>
              <div className="text-[10px] uppercase tracking-wider opacity-60">Reporting hospitals</div>
              <div className="font-mono text-lg font-bold">{Number(national.hospital_count).toLocaleString()}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider opacity-60">U.S. min</div>
              <div className="font-mono text-lg">{Number(national.min_score).toFixed(1)}{meta.unit}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider opacity-60" style={{ color: '#C5A55A' }}>U.S. median</div>
              <div className="font-mono text-lg font-bold" style={{ color: '#C5A55A' }}>{Number(national.median_score).toFixed(1)}{meta.unit}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider opacity-60">Middle 50%</div>
              <div className="font-mono text-sm">{Number(national.p25_score).toFixed(1)} – {Number(national.p75_score).toFixed(1)}{meta.unit}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider opacity-60">U.S. max</div>
              <div className="font-mono text-lg">{Number(national.max_score).toFixed(1)}{meta.unit}</div>
            </div>
          </Card>
        )}

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search hospital or city…" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} className="pl-9" />
          </div>
          <select value={stateFilter} onChange={e => { setStateFilter(e.target.value); setPage(1); }} className="text-sm rounded-md border px-3 py-2 bg-background">
            <option value="all">All states</option>
            {states.map(s => <option key={s} value={s}>{STATE_NAMES[s] || s}</option>)}
          </select>
          <Button variant="outline" size="sm" onClick={() => setSortDir(d => d === 'best' ? 'worst' : d === 'worst' ? 'name' : 'best')} className="gap-1">
            <ArrowUpDown className="w-3.5 h-3.5" />
            {sortDir === 'best' ? `Best ${meta.lowerIsBetter ? '(lowest)' : '(highest)'} first` : sortDir === 'worst' ? `Worst ${meta.lowerIsBetter ? '(highest)' : '(lowest)'} first` : 'Alphabetical'}
          </Button>
        </div>

        {/* Result count */}
        <div className="flex items-center justify-between text-sm text-muted-foreground mb-2">
          <span>
            {filtered.length.toLocaleString()} hospital{filtered.length === 1 ? '' : 's'}
            {filtered.length > PAGE && ` · page ${page} of ${totalPages}`}
          </span>
          {totalPages > 1 && (
            <div className="flex gap-1">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>Prev</Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Next</Button>
            </div>
          )}
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 animate-spin" /></div>
        ) : (
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead style={{ background: isDark ? 'rgba(0,18,56,0.55)' : 'rgba(245,235,215,0.40)' }}>
                  <tr>
                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider opacity-60 font-semibold">#</th>
                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider opacity-60 font-semibold">Hospital</th>
                    <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider opacity-60 font-semibold">Location</th>
                    <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider opacity-60 font-semibold">Score</th>
                    <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider opacity-60 font-semibold">vs U.S. median</th>
                    <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider opacity-60 font-semibold">vs state median</th>
                    <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider opacity-60 font-semibold">Patients</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((r, i) => {
                    const tone = toneFor(r.score);
                    const cls = toneColors[tone as 'good' | 'bad' | 'neutral'];
                    const stateAgg = r.hospitals!.state ? stateAggs[r.hospitals!.state] : null;
                    const vsNation = national?.median_score != null ? r.score - Number(national.median_score) : null;
                    const vsState = stateAgg?.median_score != null ? r.score - Number(stateAgg.median_score) : null;
                    return (
                      <tr key={r.hospital_id + i} className="border-t hover:bg-muted/30">
                        <td className="px-3 py-2 text-xs opacity-50 tabular-nums">{(page - 1) * PAGE + i + 1}</td>
                        <td className="px-3 py-2 font-medium">
                          <Link to={`/hospital/${r.hospital_id}/quality`} className="hover:underline">
                            {titleCaseHospital(r.hospitals!.name)}
                          </Link>
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">
                          <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{titleCaseHospital(r.hospitals!.city || '')}, {r.hospitals!.state}</span>
                        </td>
                        <td className={`px-3 py-2 text-right font-mono font-bold tabular-nums ${cls}`}>{r.score.toFixed(1)}{meta.unit}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs">
                          {vsNation != null ? (
                            <span className={meta.lowerIsBetter
                              ? (vsNation < 0 ? 'text-emerald-500' : vsNation > 0 ? 'text-red-500' : 'opacity-50')
                              : (vsNation > 0 ? 'text-emerald-500' : vsNation < 0 ? 'text-red-500' : 'opacity-50')
                            }>
                              {vsNation > 0 ? '+' : ''}{vsNation.toFixed(2)}
                            </span>
                          ) : <span className="opacity-30">—</span>}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-xs">
                          {vsState != null ? (
                            <span className={meta.lowerIsBetter
                              ? (vsState < 0 ? 'text-emerald-500' : vsState > 0 ? 'text-red-500' : 'opacity-50')
                              : (vsState > 0 ? 'text-emerald-500' : vsState < 0 ? 'text-red-500' : 'opacity-50')
                            }>
                              {vsState > 0 ? '+' : ''}{vsState.toFixed(2)}
                            </span>
                          ) : <span className="opacity-30">—</span>}
                        </td>
                        <td className="px-3 py-2 text-right font-mono text-xs opacity-60 tabular-nums">{r.denominator ? Number(r.denominator).toLocaleString() : '—'}</td>
                        <td className="px-3 py-2 text-right">
                          <Link to={`/hospital/${r.hospital_id}/quality`} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
                            View <ExternalLink className="w-3 h-3" />
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {filtered.length === 0 && !isLoading && (
              <div className="py-12 text-center text-muted-foreground text-sm">No hospitals match your filters.</div>
            )}
          </Card>
        )}

        {/* Bottom pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>Previous</Button>
            <span className="text-sm text-muted-foreground">page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Next</Button>
          </div>
        )}
      </div>
    </div>
  );
}
